import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { UiLocale } from '../../shared/models';
import { en } from './en';
import { terminology, type TerminologyKey } from './terminology';
import { zhCN, type TranslationKey } from './zh-CN';

const MANUAL_LOCALE_KEY = 'menu-lab-ui-locale';

function validLocale(value: string | null): value is UiLocale {
  return value === 'zh-CN' || value === 'en';
}

function browserLocale(): UiLocale {
  return navigator.language.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en';
}

function initialLocale(): UiLocale {
  const stored = localStorage.getItem(MANUAL_LOCALE_KEY);
  return validLocale(stored) ? stored : browserLocale();
}

interface I18nValue {
  locale: UiLocale;
  t: (key: TranslationKey) => string;
  setLocale: (locale: UiLocale) => void;
  applyAccountLocale: (locale: UiLocale | null) => void;
  hasManualLocale: () => boolean;
}

const I18nContext = createContext<I18nValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<UiLocale>(initialLocale);
  const value = useMemo<I18nValue>(() => ({
    locale,
    t: key => locale === 'zh-CN' ? zhCN[key] : en[key],
    setLocale: next => {
      localStorage.setItem(MANUAL_LOCALE_KEY, next);
      setLocaleState(next);
    },
    applyAccountLocale: accountLocale => {
      if (!localStorage.getItem(MANUAL_LOCALE_KEY) && accountLocale) setLocaleState(accountLocale);
    },
    hasManualLocale: () => validLocale(localStorage.getItem(MANUAL_LOCALE_KEY))
  }), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used within LocaleProvider');
  return value;
}

export function BilingualTerm({ term }: { term: TerminologyKey }) {
  const { locale } = useI18n();
  const value = terminology[term];
  const primary = value[locale];
  const secondary = value[locale === 'zh-CN' ? 'en' : 'zh-CN'];
  return <span className="bilingual-term" aria-label={primary}>
    <span>{primary}</span><small aria-hidden="true">{secondary}</small>
  </span>;
}

export function LocaleSelect({ onChange, disabled = false }: { onChange?: (locale: UiLocale) => void; disabled?: boolean }) {
  const { locale, setLocale, t } = useI18n();
  return <label className="locale-control">
    <span>{t('common.language')}</span>
    <select disabled={disabled} value={locale} onChange={event => {
      const next = event.target.value as UiLocale;
      setLocale(next);
      onChange?.(next);
    }}>
      <option value="zh-CN">{t('common.zh')}</option>
      <option value="en">{t('common.en')}</option>
    </select>
  </label>;
}
